import { usePut } from "@albertine/api-client";
import type { UpdateBookingImagesRequest } from "@albertine/types/openapi/lmt";
import { ImageStrip } from "@prototypes/image-strip/ImageStrip";
import { ImageStripEmptyPlaceholder } from "@prototypes/image-strip/ImageStripEmptyPlaceholder";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { Plus } from "lucide-react";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import type { Attachment, ImageAttachment } from "../../common/types/Attachment";
import type { Booking } from "../../common/types/Booking";
import {
    requireSupportedImageFile,
    supportedImageAccept,
} from "../../common/utils/image.util";
import { bookingImagesRef } from "../../FirebaseBackend";
import { uploadFile } from "../../FirebaseBackendActions";
import { randomUUIDFilename } from "../../utils/file.util";
import { takeSupportedImageFiles } from "../../utils/imageUpload.util";
import Label from "../Common/Label";
import HandleFileUpload from "../HandleFileUpload";
import {
    arrangedBookingImages,
    type BookingStripImage,
    bookingAttachmentsFromStrip,
    toBookingStripImages,
} from "./bookingImageStrip";
import "./EditBookingImages.css";

type Props = {
    booking: Booking;
    invalidInput?: boolean;
};

async function uploadImages(
    booking: Booking,
    files: File[],
): Promise<Attachment[] | undefined> {
    if (files.length === 0) return undefined;

    try {
        const uploads = files.map((image) => {
            requireSupportedImageFile(image);
            return uploadFile(
                image,
                bookingImagesRef(booking.id, randomUUIDFilename()),
            );
        });

        const uploaded = await Promise.all(uploads);
        return uploaded;
    } catch (error) {
        Sentry.captureException(error, {
            extra: { message: "LMT failed to load upload images to booking" },
        });
        window.alert(error);
    }
    return undefined;
}

export function EditBookingImages({ booking, invalidInput = false }: Props) {
    const [pendingUploadCount, setPendingUploadCount] = useState(0);
    const [busyIds, setBusyIds] = useState<string[]>([]);
    const handleFileUploadRef = useRef<{ triggerFileUpload: () => void }>(null);
    const { mutateAsync: putBookingImages } = usePut("updateBookingImages");

    const images = arrangedBookingImages(booking);
    const stripImages = toBookingStripImages(images);
    const noImages = stripImages.length === 0;

    function updateThisBookingImages(json: UpdateBookingImagesRequest) {
        return putBookingImages({
            param: { bookingId: booking.id },
            json,
        });
    }

    async function persistImages(next: ImageAttachment[]) {
        await updateThisBookingImages({
            imageURL: next[0]?.url ?? null,
            croppedImageURL: next[0]?.croppedImageURL ?? null,
            images: next,
        });
    }

    async function onImagesUpload(uploaded: Attachment[]) {
        await updateThisBookingImages({
            imageURL: booking.imageURL || uploaded[0]?.url || null,
            images: [...(booking.images ?? []), ...uploaded],
        });
    }

    async function onStripChange(next: BookingStripImage[]) {
        const attachments = bookingAttachmentsFromStrip(next);
        const removedIds = stripImages
            .filter((image) => !next.some((item) => item.id === image.id))
            .map((image) => image.id);

        if (removedIds.length > 0 && !window.confirm("Remove image?")) {
            return;
        }

        if (removedIds.length > 0) {
            setBusyIds(removedIds);
        }

        try {
            await persistImages(attachments);
        } finally {
            setBusyIds([]);
        }
    }

    return (
        <div className="flex w-full min-w-0 flex-col gap-3">
            <div className="flex justify-end">
                <Button
                    type="button"
                    onClick={() => handleFileUploadRef.current?.triggerFileUpload()}
                >
                    <Plus data-icon="inline-start" />
                    Upload
                </Button>
            </div>
            <HandleFileUpload
                dragAndDropLabel="Drag and drop images here..."
                ref={handleFileUploadRef}
                accept={supportedImageAccept}
                onUpload={async (files) => {
                    const supported = takeSupportedImageFiles(files);
                    if (supported.length === 0) return;

                    setPendingUploadCount(supported.length);
                    try {
                        const uploaded = await uploadImages(booking, supported);
                        if (uploaded) await onImagesUpload(uploaded);
                    } finally {
                        setPendingUploadCount(0);
                    }
                }}
                className={classNames(
                    "edit-booking-images min-h-[250px] w-full",
                    noImages && "edit-booking__image--empty",
                    invalidInput && "invalid-input",
                )}
            >
                {noImages && pendingUploadCount === 0 ? (
                    <ImageStripEmptyPlaceholder
                        invalid={invalidInput}
                        onClick={() => handleFileUploadRef.current?.triggerFileUpload()}
                    >
                        <Label.Medium.Light.N60>
                            Drag and drop or click to add images
                        </Label.Medium.Light.N60>
                    </ImageStripEmptyPlaceholder>
                ) : (
                    // biome-ignore lint/a11y/noStaticElementInteractions: stop the drop-zone click from opening the file picker
                    // biome-ignore lint/a11y/useKeyWithClickEvents: click-only guard for the upload wrapper
                    <div
                        className="w-full min-w-0 cursor-auto"
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <ImageStrip
                            images={stripImages}
                            editable
                            busyIds={busyIds}
                            pendingUploadCount={pendingUploadCount}
                            onReorder={onStripChange}
                            className="min-w-0"
                        />
                    </div>
                )}
            </HandleFileUpload>
        </div>
    );
}

export default EditBookingImages;
